import React, { useEffect, useState, useRef, ReactElement } from 'react';
import { useLocation, Location } from 'react-router-dom';
import ErrorBoundaryComponent from '../../Pages/ErrorBoundaryComponent/ErrorBoundaryComponent';

interface ErrorBoundaryProps {
  children: React.ReactNode;
}

const ErrorBoundary: React.FC<ErrorBoundaryProps> = ({ children }) => {
  const [errorCode, setErrorCode] = useState<string | null>(null);
  const [errorInfo, setErrorInfo] = useState<ErrorEvent | PromiseRejectionEvent | null>(null);
  const location: Location = useLocation();
  const prevLocation = useRef<Location>(location);

  const handleError = (error: string, errorInfo: ErrorEvent | PromiseRejectionEvent) => {
    setErrorCode(error);
    setErrorInfo(errorInfo);
  };

  useEffect(() => {
    if (location.key !== prevLocation.current.key) {
      setErrorCode(null);
      setErrorInfo(null);
      prevLocation.current = location;
    }
  }, [location]);

  useEffect(() => {
    const handleComponentError = (event: PromiseRejectionEvent | ErrorEvent) => {
      if ('reason' in event) {
        handleError(event?.reason?.message || event.reason, event);
      } else {
        handleError(event.error, event);
      }
    };

    window.addEventListener('error', handleComponentError);
    window.addEventListener('unhandledrejection', handleComponentError);

    return () => {
      window.removeEventListener('error', handleComponentError);
      window.removeEventListener('unhandledrejection', handleComponentError);
    };
  }, []);

  if (errorCode) {
    return <ErrorBoundaryComponent errorCode={errorCode} />;
  }

  return children as ReactElement<any, any>;
};

export default ErrorBoundary;
